import { useQuery } from '@tanstack/react-query';
import { useParams } from '@/providers/RouterShim';
import type { RequestStateQuery } from '../../@types/RequestStateQuery';
import type { MarketCode } from '../../@types/Token';
import { getJSON } from '../../fetch';
import useAuth from '../../hooks/useAuth';
import type { VehicleAdStation, VehicleAdStatus } from './useVehicle';
import type {
  VehicleListItemEnrichment,
  VehicleListItemOdometer,
  VehicleListItemProgressState,
  VehicleListItemPurchaseInfoState,
  VehicleListItemTask,
} from './useVehicles';

export const VEHICLE_EXPLORE_QUERY = '/:processFlowUri/search/:branchId/vehicles/:id';
interface VehicleExplorAdImage {
  url: string;
}

interface VehicleExploreAd {
  images?: VehicleExplorAdImage[];
  isAuctionItem: boolean;
  isDemoVersion: boolean;
  price: number;
  shortDescription: string;
  title: string;
}

interface VehicleExploreVehicle {
  chassisType: string;
  colorName: string;
  drivingWheel: string;
  fuelType: string;
  gearboxType: string;
  manufacturer: string;
  modelName: string;
  modelSeries: string;
  modelYear: number;
  odometerReading: VehicleListItemOdometer;
  registrationNumber: string;
  salesName: string;
  segment: string;
  vin: string;
}

type VehicleExploreSalesOrderStatus = 'DRAFT';

export interface VehicleExploreSalesOrder {
  id: string;
  status: VehicleExploreSalesOrderStatus;
}

export interface VehicleExploreSales {
  hasOrders: boolean;
  isReserved: boolean;
  orders?: VehicleExploreSalesOrder[];
}

export interface VehicleExplore {
  ad: VehicleExploreAd;
  archivedAt?: string | null;
  arrivedAt?: string | null;
  branchId: string;
  createdAt: string;
  createdBy: string;
  enrichment?: VehicleListItemEnrichment;
  id: string;
  iterationId?: string;
  lastUpdatedAt: string;
  lastUpdatedBy: string;
  managedBy: string;
  marketCode: MarketCode;
  organizationId: string;
  publishedAt?: string | null;
  purchaseInfo?: VehicleListItemPurchaseInfoState;
  status: VehicleAdStatus;
  vehicle: VehicleExploreVehicle;
  visibility: string;
  progressState?: VehicleListItemProgressState | null;
  station?: VehicleAdStation;
  tasks?: VehicleListItemTask[];
  sales?: VehicleExploreSales;
}

interface VehicleExploreResponse {
  duration_in_ms: 90;
  total_hit_count: 1;
  hits: VehicleExplore[];
}

interface UseVehicleExploreOptions {
  id?: string;
  archived?: boolean;
  disabled?: boolean;
}

const useVehicleExplore = (
  options: UseVehicleExploreOptions | string | undefined,
): [VehicleExplore[] | undefined, RequestStateQuery] => {
  const { branchId } = useParams();
  const { getToken } = useAuth();

  // Support both old string API and new options object
  const opts = typeof options === 'string' ? { id: options } : options;
  const id = opts?.id;
  const archived = opts?.archived;
  const disabled = opts?.disabled;

  const { data, isLoading, isFetching, isSuccess, isError } = useQuery<VehicleExploreResponse>({
    queryKey: [VEHICLE_EXPLORE_QUERY, branchId, id, archived],
    queryFn: async () => {
      const url = new URL(
        `${process.env.NEXT_PUBLIC_PROCESS_FLOW_URI}/explore/${branchId}/vehicles/${id}`,
      );
      if (archived !== undefined) {
        url.searchParams.append('archived', String(archived));
      }
      return await getJSON<VehicleExploreResponse>(url.toString(), getToken()!);
    },
    enabled: !!id && !disabled,
    gcTime: 1 * 60 * 1000,
    staleTime: 1 * 60 * 1000,
  });
  return [data?.hits, { isLoading, isFetching, isSuccess, isError }];
};

export default useVehicleExplore;
